import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { hasValue, reduceToObject } from '@fastre/core/helperFunctions/array'
import { capitalize, toCurrency } from '@fastre/core/helperFunctions/string'
import { InternalConjunctionalAgencySchema } from '@fastre/core/schemas/conjunionalAgency'
import { InternalTransactionSchema } from '@fastre/core/schemas/contract'
import { BalanceSheetSchema } from '@fastre/core/schemas/listing'
import { zodResolver } from '@hookform/resolvers/zod'
import { DragHandleRounded, EditRounded } from '@mui/icons-material'
import { Box, Button, CircularProgress, IconButton, Link, Stack, Table, Typography } from '@mui/joy'
import { useApi } from 'api'
import { useConjunctionalAgenciesApi, useFindUserFromId, useOrgIntegrationsApi } from 'apiProviders'
import { useUserData } from 'auth'
import Modal, { dontCloseOnBackgroundClick } from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import { SortableList } from 'components/sortableList'
import { format } from 'date-fns'
import { useMaybeState } from 'helperFunctions/react'
import OptionalTooltip from 'links/optionalTooltip'
import { useEditMode } from 'listings/editMode'
import { pick, prop, propEq, sum } from 'ramda'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useListingContext } from '../listingProvider'
import ConfirmPaidModal from './confirmPaidModal'
import EditTransactionModal from './editTransactionModal'
import FundDisbursement from './fundDisbursement'

interface RowProps {
    transaction: InternalTransactionSchemaWithBalance
    transactionIndex: number
    setEditTransaction: (transaction: InternalTransactionSchema) => void
    setConfirmTransaction: (val: { transaction: InternalTransactionSchema; transactionIndex: number }) => void
    conjunctionalAgency?: InternalConjunctionalAgencySchema
    showConjunctionalCell: boolean
}

const Row = ({
    transaction,
    transactionIndex,
    setEditTransaction,
    setConfirmTransaction,
    conjunctionalAgency,
    showConjunctionalCell,
}: RowProps) => {
    const api = useApi()
    const { listing, setListing } = useListingContext()
    const findUserFromId = useFindUserFromId()
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: transaction.id })
    const { user } = useUserData()
    const { editMode } = useEditMode()
    const integrationsApi = useOrgIntegrationsApi()
    const officeXeroConnected = integrationsApi.data?.xero != undefined

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const [createInvoiceLoading, setCreateInvoiceLoading] = useState(false)

    return (
        <tr
            ref={setNodeRef}
            style={style}
        >
            {editMode && (
                <td
                    style={{
                        border: 'none',
                        background: 'none',
                    }}
                >
                    <IconButton
                        {...listeners}
                        {...attributes}
                    >
                        <DragHandleRounded />
                    </IconButton>
                </td>
            )}
            <td>{transaction.date && format(new Date(transaction.date), 'dd/MM/yy')}</td>
            <td>{transaction.description}</td>
            <td align="right">{transaction.type == 'credit' && toCurrency(transaction.amount)}</td>
            <td align="right">{transaction.type == 'debit' && toCurrency(transaction.amount)}</td>
            <td align="right">{toCurrency(transaction.balance)}</td>
            <td align="center">
                {transaction.paid?.userId ? (
                    <>
                        {findUserFromId(transaction.paid.userId)
                            .map(x => (x.firstName[0] + x.lastName[0]).toUpperCase())
                            .orSome('')}
                    </>
                ) : editMode ? (
                    <Link
                        onClick={() =>
                            setConfirmTransaction({
                                transaction,
                                transactionIndex,
                            })
                        }
                    >
                        Pending
                    </Link>
                ) : (
                    'Pending'
                )}
            </td>
            {showConjunctionalCell && (
                <td>
                    {transaction.externalRef == 'officeCommission' &&
                        (transaction.xeroInvoice ? (
                            <Link onClick={() => window.open(transaction.xeroInvoice!.url, '_blank')}>
                                Invoiced
                            </Link>
                        ) : createInvoiceLoading ? (
                            <CircularProgress sx={{ '--CircularProgress-size': '20px' }} />
                        ) : (
                            <OptionalTooltip
                                show={!officeXeroConnected}
                                title="Office does not have Xero setup"
                            >
                                <Box>
                                    <Link
                                        onClick={async () => {
                                            setCreateInvoiceLoading(true)
                                            try {
                                                const updatedItem = await api
                                                    .post(
                                                        `/listing/sale/${listing.listingId}/xero/officecommissioninvoice`,
                                                        {
                                                            transactionId: transaction.id,
                                                        },
                                                    )
                                                    .then(prop('data'))
                                                setListing({
                                                    ...listing,
                                                    balanceSheet: listing.balanceSheet!.map(x =>
                                                        x.id == updatedItem.id ? updatedItem : x,
                                                    ),
                                                })
                                            } catch (e) {
                                                console.error(e)
                                            } finally {
                                                setCreateInvoiceLoading(false)
                                            }
                                        }}
                                        disabled={!officeXeroConnected}
                                    >
                                        Create Invoice
                                    </Link>
                                </Box>
                            </OptionalTooltip>
                        ))}
                    {transaction.externalRef == 'agentCommission' &&
                        (transaction.xeroInvoice ? (
                            <Link onClick={() => window.open(transaction.xeroInvoice!.url, '_blank')}>
                                Invoiced
                            </Link>
                        ) : (
                            <>
                                {createInvoiceLoading ? (
                                    <CircularProgress
                                        size="sm"
                                        sx={{ '--CircularProgress-size': '20px' }}
                                    />
                                ) : (
                                    <OptionalTooltip
                                        show={conjunctionalAgency?.xero == undefined}
                                        title="Conjunctional agency does not have Xero setup"
                                    >
                                        <Box>
                                            <Link
                                                onClick={async () => {
                                                    setCreateInvoiceLoading(true)
                                                    try {
                                                        const updatedItem = await api
                                                            .post(
                                                                `/listing/sale/${listing.listingId}/xero/rcti`,
                                                                {
                                                                    transactionId: transaction.id,
                                                                },
                                                            )
                                                            .then(prop('data'))
                                                        setListing({
                                                            ...listing,
                                                            balanceSheet: listing.balanceSheet!.map(x =>
                                                                x.id == updatedItem.id ? updatedItem : x,
                                                            ),
                                                        })
                                                    } catch (e) {
                                                        console.error(e)
                                                    } finally {
                                                        setCreateInvoiceLoading(false)
                                                    }
                                                }}
                                                disabled={conjunctionalAgency?.xero == undefined}
                                            >
                                                Create Invoice
                                            </Link>
                                        </Box>
                                    </OptionalTooltip>
                                )}
                            </>
                        ))}
                </td>
            )}
            {editMode && (
                <td>
                    <Box>
                        <IconButton
                            size="sm"
                            onClick={() => {
                                setEditTransaction(transaction)
                            }}
                        >
                            <EditRounded fontSize="small" />
                        </IconButton>
                    </Box>
                </td>
            )}
        </tr>
    )
}

type InternalTransactionSchemaWithBalance = InternalTransactionSchema & { balance: number }

export default forwardRef(({}, ref) => {
    const { user } = useUserData()
    const api = useApi()
    const { listing, setListing } = useListingContext()
    const showSnack = useShowSnack()
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()
    const findUserFromId = useFindUserFromId()
    const { editMode } = useEditMode()

    const agents = listing.agents.map(({ userId }) => findUserFromId(userId).orUndefined()).filter(hasValue)
    const conjunctionalAgencies = agents
        .map(user => {
            const conjunctionalAgency =
                conjunctionalAgenciesApi.data?.find(
                    propEq('conjunctionalAgencyId', user.conjunctionalAgencyId),
                ) ?? undefined

            if (conjunctionalAgency) {
                return {
                    [user.userId]: conjunctionalAgency,
                }
            }
        })
        .filter(hasValue)
        .reduce(reduceToObject, {})

    const [editTransaction, setEditTransaction] = useMaybeState<any>()
    const [confirmTransaction, setConfirmTransaction] = useMaybeState<{
        transaction: InternalTransactionSchema
        transactionIndex: number
    }>()

    const hookForm = useForm<BalanceSheetSchema>({
        defaultValues: {
            balanceSheet: [],
        },
        resolver: zodResolver(BalanceSheetSchema),
    })

    const { control, handleSubmit, formState, getValues, setValue, trigger, reset, watch } = hookForm

    useEffect(() => {
        reset(pick(['balanceSheet'], listing))
    }, [listing.balanceSheet])

    const {
        fields: balanceSheet,
        append: appendBalance,
        remove: removeBalance,
    } = useFieldArray({
        control,
        name: 'balanceSheet',
    })
    const balanceSheetWatch = watch('balanceSheet', []) as InternalTransactionSchema[]

    const [loading, setLoading] = useState(false)

    const onSubmit: SubmitHandler<BalanceSheetSchema> = async data => {
        setLoading(true)
        try {
            const updatedListing = await api
                .post(`/listing/sale/${listing.listingId}/updatebalancesheet`, data)
                .then(prop('data'))
            setListing(updatedListing)
            reset(pick(['balanceSheet'], updatedListing))
            showSnack('Trust fund saved', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error saving trust fund', 'danger')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmitPromise = (): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            handleSubmit(
                async data => {
                    try {
                        await onSubmit(data)
                        resolve(true)
                    } catch (error) {
                        resolve(false)
                    }
                },
                error => {
                    resolve(false)
                },
            )()
        })
    }

    // Expose the `saveForm` method to the parent via ref
    useImperativeHandle(ref, () => ({
        saveForm: handleSubmitPromise,
        resetForm: () => reset(pick(['balanceSheet'], listing)),
    }))

    const balanceSheetWatch_withBalance = balanceSheetWatch.reduce(
        (acc: InternalTransactionSchemaWithBalance[], transaction: InternalTransactionSchema) => [
            ...acc,
            {
                ...transaction,
                balance:
                    (acc[acc.length - 1]?.balance ?? 0) +
                    transaction.amount * (transaction.type == 'credit' ? 1 : -1),
            } as any,
        ],
        [],
    ) as InternalTransactionSchemaWithBalance[]

    if (Object.keys(formState.errors).length > 0) {
        console.log('errors', formState.errors)
    }

    return (
        <>
            <FormProvider {...hookForm}>
                <form
                    noValidate
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Stack
                        gap={2}
                        maxWidth={1200}
                    >
                        {listing.marketingPaymentMethod && (
                            <Box>
                                <Typography>
                                    Marketing package: $
                                    <b>
                                        {sum(
                                            listing.marketingPackage?.packageItems.map(prop('itemPrice')) ?? [
                                                0,
                                            ],
                                        )}
                                    </b>
                                </Typography>
                                <Typography>
                                    Payment method: <b>{capitalize(listing.marketingPaymentMethod)}</b>
                                </Typography>
                                {listing.marketingPaymentStatus && (
                                    <Typography>
                                        Payment status:{' '}
                                        <b>{capitalize(listing.marketingPaymentStatus.paymentStatus)}</b>
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Table borderAxis="both">
                            <thead>
                                <tr>
                                    {editMode && (
                                        <th
                                            style={{
                                                width: '60px',
                                                border: 'none',
                                                background: 'none',
                                            }}
                                        />
                                    )}
                                    <th>Date</th>
                                    <th>Description</th>
                                    <th align="right">Credit</th>
                                    <th align="right">Debit</th>
                                    <th align="right">Balance</th>
                                    <th align="center">Paid/Receipted</th>
                                    {user.permissions.includes('agentStatement.generatePayroll') &&
                                        editMode &&
                                        Object.keys(conjunctionalAgencies).length > 0 && <th />}
                                    {editMode && <th style={{ width: 50 }} />}
                                </tr>
                            </thead>
                            <tbody>
                                <SortableList
                                    items={balanceSheetWatch_withBalance}
                                    onChange={newItems => {
                                        setValue('balanceSheet', newItems)
                                    }}
                                    renderItem={(transaction, transactionIndex) => {
                                        const conjunctionalAgency =
                                            transaction.externalRef == 'agentCommission'
                                                ? conjunctionalAgencies[transaction.userId]
                                                : undefined

                                        return (
                                            <Row
                                                key={transaction.id}
                                                transaction={transaction}
                                                transactionIndex={transactionIndex}
                                                setEditTransaction={setEditTransaction}
                                                setConfirmTransaction={setConfirmTransaction}
                                                conjunctionalAgency={conjunctionalAgency}
                                                showConjunctionalCell={
                                                    user.permissions.includes(
                                                        'agentStatement.generatePayroll',
                                                    ) && Object.keys(conjunctionalAgencies).length > 0
                                                }
                                            />
                                        )
                                    }}
                                />
                            </tbody>
                        </Table>
                        {user.permissions.includes('fit.edit') && editMode && (
                            <>
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        onClick={() => {
                                            setEditTransaction({
                                                //date: Date.now(),
                                                //description: '',
                                                //amount: 0,
                                                //type: 'credit',
                                            })
                                        }}
                                        variant="outlined"
                                    >
                                        Add Transaction
                                    </Button>
                                </Box>
                            </>
                        )}
                        {user.permissions.includes('fundDisbursement.send') &&
                            (import.meta.env.VITE_APP_STAGE == 'uat' || import.meta.env.DEV) && (
                                <Box sx={{ mt: 6 }}>
                                    <FundDisbursement />
                                </Box>
                            )}
                    </Stack>
                </form>
            </FormProvider>
            <Modal
                open={editTransaction.isSome()}
                onClose={dontCloseOnBackgroundClick(() => setEditTransaction(undefined))}
            >
                {editTransaction
                    .map(transaction => (
                        <EditTransactionModal
                            transaction={transaction}
                            close={() => setEditTransaction(undefined)}
                            save={(data: InternalTransactionSchema) => {
                                if (transaction.id) {
                                    const index = balanceSheetWatch.findIndex(x => x.id == transaction.id)
                                    console.log('index', index)
                                    setValue(`balanceSheet.${index}`, data)
                                } else {
                                    appendBalance(data)
                                }
                            }}
                            onDelete={() =>
                                removeBalance(balanceSheetWatch.findIndex(x => x.id == transaction.id))
                            }
                        />
                    ))
                    .orSome(<></>)}
            </Modal>
            <Modal
                open={confirmTransaction.isSome()}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') {
                        return
                    }
                    setConfirmTransaction(undefined)
                }}
            >
                {confirmTransaction
                    .map(({ transaction, transactionIndex }) => (
                        <ConfirmPaidModal
                            key={transactionIndex}
                            transaction={transaction}
                            close={(confirm, date) => {
                                if (confirm) {
                                    setValue(`balanceSheet.${transactionIndex}`, {
                                        ...transaction,
                                        date,
                                        paid: {
                                            time: Date.now(),
                                            userId: user.userId,
                                        },
                                    })
                                }
                                setConfirmTransaction(undefined)
                            }}
                        />
                    ))
                    .orSome(<></>)}
            </Modal>
        </>
    )
})
